<template>
  <router-link :to="getItemUrl(item)">
    <v-card class="hover:shadow-card" height="auto">
      <div class="prompt-thumb-wrapper">
        <RfPromotionThumbnail :key="item.id" :item="item" />
      </div>
      <div class="prompt-detail-wrapper min-w-px">
        <div class="flex min-w-px pr-4">
          <div class="flex w-full min-w-px flex-col">
            <h1 class="max-w-full truncate">
              {{ item.name }}
            </h1>
            <div>{{ item.description }}</div>
          </div>
          <div class="flex h-8 flex-0 items-center gap-2">
            <RfSegmentBucketRange
              class="inline-flex items-center gap-1 text-blue-50"
              :showWarning="item.segments.length > 1"
              :bucketRange="bucketRange"
            />
            <v-tooltip v-if="openAssignTrafficModal" location="bottom">
              <template #activator="{ props }">
                <span v-bind="props" class="inline-flex" @click.stop.prevent>
                  <v-btn
                    variant="flat"
                    size="small"
                    :disabled="!!disableAssignTraffic"
                    :aria-label="disableAssignTraffic || 'Assign traffic'"
                    @click.stop.prevent="
                      () => !disableAssignTraffic && openAssignTrafficModal(item)
                    "
                  >
                    <v-icon size="16"> fa:fas fa-random </v-icon>
                  </v-btn>
                </span>
              </template>
              <span>{{ disableAssignTraffic || "Assign traffic" }}</span>
            </v-tooltip>
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  variant="flat"
                  size="small"
                  @click.stop.prevent="exportSinglePromptToCsv(item, currChartSrc)"
                >
                  <v-icon size="20"> md:download </v-icon>
                </v-btn>
              </template>
              <span>Export to CSV</span>
            </v-tooltip>
          </div>
        </div>

        <div v-if="item.sequence_id && !hideSequence">
          Guide:
          <router-link :to="sequenceLink(item)">{{ item.sequence_name }}</router-link>
        </div>
        <div class="promo-list-view-details">
          <RfPromoDetailsRow title="Type">
            <template #promoinfo>
              {{ typeWithDevice(item) }}
            </template>
          </RfPromoDetailsRow>
          <RfPromoDetailsRow v-if="!hideSegment" title="Segments">
            <template #promoinfo>
              <div v-if="item.sequence_id" class="list-promo-detail-text">
                Inherited from:
                <router-link :to="sequenceLink(item)">{{ item.sequence_name }}</router-link>
              </div>
              <div v-else class="list-promo-detail-text">
                <template v-if="item.segments?.length">
                  <router-link
                    v-for="segment in item.segments"
                    :key="segment.name"
                    class="list-promo-detail--segments-link"
                    :to="segmentLink(segment)"
                    >{{ segment.name }}</router-link
                  >
                </template>
                <span v-else>-</span>
              </div>
            </template>
          </RfPromoDetailsRow>
          <RfPromoDetailsRow v-if="!hideSchedule" title="Schedule">
            <template #promoinfo>{{ scheduleDisplay(item) }}</template>
          </RfPromoDetailsRow>
          <RfPromoDetailsRow v-if="!hideStatus" title="Status">
            <template #promoinfo>
              <RfPromoPublishStatus :item="item" />
            </template>
          </RfPromoDetailsRow>
        </div>
      </div>
      <div class="prompt-stats-wrapper">
        <RfPlacementsStats
          :model="item"
          :metricsKey="currChartSrc"
          :autoHeight="true"
          :preprocessedGoals="preprocessedGoals"
          class="rf-metrics-arrow-responsive"
        />
      </div>
    </v-card>
  </router-link>
</template>

<script>
import PromoTypeMixin from "@/utils/PromoTypeMixin";
import RfPlacementsStats from "@/components/RfPlacements/RfPlacementsStats.vue";
import RfPromotionThumbnail from "@/components/RfPromotions/RfPromotionThumbnail.vue";
import RfPromoDetailsRow from "@/components/RfPromotions/RfPromoDetailsRow.vue";
import RfPromoPublishStatus from "@/components/RfPromotions/RfPromoPublishStatus.vue";
import RfSegmentBucketRange from "@/components/RfSegments/RfSegmentBucketRange.vue";
import { exportSinglePromptToCsv } from "@/utils/CsvUtils";
import { dateDisplay } from "@/utils/DateDisplayUtils";

export default {
  name: "RfPromotionsListViewItem",
  components: {
    RfPlacementsStats,
    RfPromotionThumbnail,
    RfPromoDetailsRow,
    RfPromoPublishStatus,
    RfSegmentBucketRange,
  },
  mixins: [PromoTypeMixin],
  props: {
    item: Object,
    currChartSrc: String,
    preprocessedGoals: { type: Boolean, default: false },
    hideSegment: { type: Boolean, default: false },
    hideSchedule: { type: Boolean, default: false },
    hideSequence: { type: Boolean, default: false },
    hideStatus: { type: Boolean, default: false },
    fromSequence: { type: Boolean, default: false },
    bucketRange: { type: Array, default: () => [] },
    openAssignTrafficModal: { type: Function },
    disableAssignTraffic: { type: [Boolean, String], default: false },
  },
  setup: () => ({ exportSinglePromptToCsv }),
  methods: {
    sequenceLink(item) {
      return { path: `/apps/${this.$route.params.aid}/experiences/${item.sequence_id}` };
    },
    segmentLink(item) {
      return { path: `/apps/${this.$route.params.aid}/segments/${item.id}` };
    },
    scheduleDisplay(item) {
      return `${dateDisplay(item.start_date)} to ${dateDisplay(item.end_date) || "∞"}`;
    },
    getItemUrl(item) {
      if (this.fromSequence && item.sequence_id) {
        return {
          path: `/apps/${this.$route.params.aid}/experiences/${item.sequence_id}/promotions/${item.id}`,
        };
      }
      return { path: `/apps/${this.$route.params.aid}/retentions/${item.id}` };
    },
  },
};
</script>
